import { CheckCircle, Close as CloseIcon, Warning } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useAdminService, UserPlan } from '../../services/admin.service';
import IndividualPlanTab from './IndividualPlanTab';

interface UserPlansModalProps {
    open: boolean;
    onClose: () => void;
    userName: string;
    userId: string;
    userPlans: UserPlan[];
    loading: boolean;
    error: string | null;
    onRefreshPlans: () => Promise<void>;
}

const UserPlansModal: React.FC<UserPlansModalProps> = ({
    open,
    onClose,
    userName,
    userId,
    userPlans,
    loading,
    error,
    onRefreshPlans,
}) => {
    const [tabValue, setTabValue] = useState(0);
    const [validatingPlanId, setValidatingPlanId] = useState<string | null>(null);
    const { validateWeDoEverything } = useAdminService();

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleValidateWeDoEverything = async (plan: UserPlan) => {
        setValidatingPlanId(plan.id);
        try {
            await validateWeDoEverything(userId);
            // Refresh the user plans data
            await onRefreshPlans();
        } catch (error) {
            console.error('Error validating We Do Everything addon:', error);
        } finally {
            setValidatingPlanId(null);
        }
    };

    // Group plans by type
    const taxReportPlans = userPlans.filter((plan) => plan.type === 'taxReport');
    const expertReviewPlans = userPlans.filter((plan) => plan.type === 'expertReview');

    // Find "We Do Everything" addon specifically
    const weDoEverythingPlans = userPlans.filter(
        (plan) => plan.type === 'addon' && plan.addonType === 'we_do_everything'
    );

    // Filter out "We Do Everything" from regular addons
    const addonPlans = userPlans.filter((plan) => plan.type === 'addon' && !(plan.addonType === 'we_do_everything'));

    const getExpiryDate = (plan: UserPlan) => {
        return plan.expiryDate || plan.validUntil || 'N/A';
    };
    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth aria-labelledby="plans-dialog-title">
            <DialogTitle
                id="plans-dialog-title"
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
                <Box>User Plans - {userName}</Box>
                <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                ) : (
                    <Box sx={{ width: '100%' }}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                            <Tabs value={tabValue} onChange={handleTabChange} aria-label="plan type tabs">
                                <Tab label="Individual Plan" />
                                <Tab label="We Do Everything" />
                                <Tab label="Tax Reports" />
                                <Tab label="Expert Reviews" />
                                <Tab label="Add-ons" />
                            </Tabs>
                        </Box>

                        {/* Individual Plan Tab */}
                        {tabValue === 0 && <IndividualPlanTab userId={userId} />}

                        {/* We Do Everything Tab */}
                        {tabValue === 1 && (
                            <Paper elevation={0} variant="outlined" sx={{ mb: 2 }}>
                                <TableContainer>
                                    <Table size="medium">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Name</TableCell>
                                                <TableCell>Purchase Date</TableCell>
                                                <TableCell>Valid Until</TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell>Pricing Type</TableCell>
                                                <TableCell>Validated</TableCell>
                                                <TableCell>Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {weDoEverythingPlans.length > 0 ? (
                                                weDoEverythingPlans.map((plan) => (
                                                    <TableRow key={plan.id}>
                                                        <TableCell>{plan.name}</TableCell>
                                                        <TableCell>
                                                            {new Date(plan.purchaseDate).toLocaleDateString()}
                                                        </TableCell>
                                                        <TableCell>
                                                            {getExpiryDate(plan) !== 'N/A'
                                                                ? new Date(getExpiryDate(plan)).toLocaleDateString()
                                                                : 'N/A'}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={plan.status}
                                                                color={
                                                                    plan.status === 'active'
                                                                        ? 'success'
                                                                        : plan.status === 'expired'
                                                                          ? 'error'
                                                                          : 'warning'
                                                                }
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={plan.isCustomPricing ? 'Custom' : 'Standard'}
                                                                color={plan.isCustomPricing ? 'warning' : 'default'}
                                                                size="small"
                                                                variant={plan.isCustomPricing ? 'filled' : 'outlined'}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={plan.validated ? 'Validated' : 'Not Validated'}
                                                                color={plan.validated ? 'success' : 'warning'}
                                                                size="small"
                                                                icon={plan.validated ? <CheckCircle /> : <Warning />}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            {!plan.validated && plan.status === 'active' && (
                                                                <Button
                                                                    variant="contained"
                                                                    color="primary"
                                                                    size="small"
                                                                    onClick={() => handleValidateWeDoEverything(plan)}
                                                                    disabled={validatingPlanId === plan.id}
                                                                    startIcon={
                                                                        validatingPlanId === plan.id ? (
                                                                            <CircularProgress size={16} />
                                                                        ) : null
                                                                    }
                                                                >
                                                                    {validatingPlanId === plan.id
                                                                        ? 'Validating...'
                                                                        : 'Mark as Validated'}
                                                                </Button>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={7} align="center">
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{ py: 2 }}
                                                        >
                                                            No "We Do Everything" plans found
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        )}

                        {/* Tax Reports Tab */}
                        {tabValue === 2 && (
                            <Paper elevation={0} variant="outlined" sx={{ mb: 2 }}>
                                <TableContainer>
                                    <Table size="medium">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Name</TableCell>
                                                <TableCell>Tax Year</TableCell>
                                                <TableCell>Package</TableCell>
                                                <TableCell>Transaction Limit</TableCell>
                                                <TableCell>Purchase Date</TableCell>
                                                <TableCell>Status</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {taxReportPlans.length > 0 ? (
                                                taxReportPlans.map((plan) => (
                                                    <TableRow key={plan.id}>
                                                        <TableCell>{plan.name}</TableCell>
                                                        <TableCell>{plan.taxYear || plan.year || 'N/A'}</TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={plan.package || 'Standard'}
                                                                size="small"
                                                                color="primary"
                                                                variant="outlined"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            {plan.transactionLimit?.toLocaleString() || 'N/A'}
                                                        </TableCell>
                                                        <TableCell>
                                                            {new Date(plan.purchaseDate).toLocaleDateString()}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={plan.status}
                                                                color={
                                                                    plan.status === 'active'
                                                                        ? 'success'
                                                                        : plan.status === 'expired'
                                                                          ? 'error'
                                                                          : 'warning'
                                                                }
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={6} align="center">
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{ py: 2 }}
                                                        >
                                                            No tax report plans found
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        )}

                        {/* Expert Reviews Tab */}
                        {tabValue === 3 && (
                            <Paper elevation={0} variant="outlined" sx={{ mb: 2 }}>
                                <TableContainer>
                                    <Table size="medium">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Name</TableCell>
                                                <TableCell>Tax Year</TableCell>
                                                <TableCell>Purchase Date</TableCell>
                                                <TableCell>Status</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {expertReviewPlans.length > 0 ? (
                                                expertReviewPlans.map((plan) => (
                                                    <TableRow key={plan.id}>
                                                        <TableCell>{plan.name}</TableCell>
                                                        <TableCell>{plan.taxYear || plan.year || 'N/A'}</TableCell>
                                                        <TableCell>
                                                            {new Date(plan.purchaseDate).toLocaleDateString()}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={plan.status}
                                                                color={
                                                                    plan.status === 'active'
                                                                        ? 'success'
                                                                        : plan.status === 'expired'
                                                                          ? 'error'
                                                                          : 'warning'
                                                                }
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={4} align="center">
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{ py: 2 }}
                                                        >
                                                            No expert review plans found
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        )}

                        {/* Add-ons Tab */}
                        {tabValue === 4 && (
                            <Paper elevation={0} variant="outlined" sx={{ mb: 2 }}>
                                <TableContainer>
                                    <Table size="medium">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Name</TableCell>
                                                <TableCell>Add-on Type</TableCell>
                                                <TableCell>Purchase Date</TableCell>
                                                <TableCell>Valid Until</TableCell>
                                                <TableCell>Status</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {addonPlans.length > 0 ? (
                                                addonPlans.map((plan) => (
                                                    <TableRow key={plan.id}>
                                                        <TableCell>{plan.name}</TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={plan.addonType || 'Standard'}
                                                                size="small"
                                                                color="secondary"
                                                                variant="outlined"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            {new Date(plan.purchaseDate).toLocaleDateString()}
                                                        </TableCell>
                                                        <TableCell>
                                                            {getExpiryDate(plan) !== 'N/A'
                                                                ? new Date(getExpiryDate(plan)).toLocaleDateString()
                                                                : 'N/A'}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={plan.status}
                                                                color={
                                                                    plan.status === 'active'
                                                                        ? 'success'
                                                                        : plan.status === 'expired'
                                                                          ? 'error'
                                                                          : 'warning'
                                                                }
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={5} align="center">
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{ py: 2 }}
                                                        >
                                                            No add-on plans found
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        )}
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default UserPlansModal;
